import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import './Port.css';
import 'mapbox-gl/dist/mapbox-gl.css';

mapboxgl.accessToken = 'pk.eyJ1IjoibWljaGFlbC1qYWNvdCIsImEiOiJjbThkY2lmOGQwbWZoMmpvZ2Z1M2xsYTRsIn0.WZPUkFIAIenQhNTTPbVVuQ'; // Replace with your Mapbox token

const Port = () => {
  const mapContainerRef = useRef(null);
  const modalRef = useRef(null); // Reference for the modal
  const legendRef = useRef(null); // Reference for the legend popup
  const [geojsonData, setGeojsonData] = useState(null); // GeoJSON data
  const [filteredGeojsonData, setFilteredGeojsonData] = useState(null); // Filtered data
  const [selectedPort, setSelectedPort] = useState(null); // Store selected port for the modal
  const [filters, setFilters] = useState({
    CleaningInPort: false,
    InspectionInPort: false,
    GeneralDivingInPort: false,
    PropellerPolishingInPort: false,
  });

  const [dropdownOpen, setDropdownOpen] = useState(false); // To toggle dropdown

  const checkTrue = (value) => {
    if (!value) return false;
    const normalizedValue = value.trim().toUpperCase();
    return normalizedValue === 'TRUE' || normalizedValue === 'YES';
  };

  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setSelectedPort(null); // Close the modal if clicked outside
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch the data once on component mount
  useEffect(() => {
    fetch('https://backend-nu-two.vercel.app/port_cleaning_status1') // Your backend endpoint
      .then((response) => response.json())
      .then((data) => {
        const geojson = {
          type: 'FeatureCollection',
          features: data
            .map((row) => {
              const lat = parseFloat(row.LAT);
              const lon = parseFloat(row.LON);
  
              if (!isNaN(lat) && !isNaN(lon)) {
                return {
                  type: 'Feature',
                  geometry: {
                    type: 'Point',
                    coordinates: [lon, lat],
                  },
                  properties: {
                    portName: row.MainPortName || 'Unknown',
                    country: row.Country || 'Unknown',
                    isAnchorage: row.isAnchorage,
                    portComment: row.PortComment || 'N/A',
                    CleaningInPort: row.CleaningInPort,
                    InspectionInPort: row.InspectionInPort,
                    GeneralDivingInPort: row.GeneralDivingInPort,
                    PropellerPolishingInPort: row.PropellerPolishingInPort,
                    AliciaBots: row.AliciaBots,
                    GreenSea: row.GreenSea,
                    DGDiving: row.DGDiving,
                    HullBot: row.HullBot,
                  
                  },
                };
              }
              return null;
            })
            .filter(Boolean), // Filter out invalid rows
        };
  
        // Log all ports with isAnchorage === 'TRUE' for debugging
        const anchoragePorts = geojson.features.filter((feature) =>
          checkTrue(feature.properties.isAnchorage)
        );
        console.log('Ports with isAnchorage = TRUE:', anchoragePorts);
  
        setGeojsonData(geojson);
        setFilteredGeojsonData(geojson); // Initially, display all ports
      })
      .catch((error) => console.error('Error fetching data:', error));
  }, []);
  

  // Filter the ports based on selected conditions
  useEffect(() => {
    if (!geojsonData) return;

    const filteredFeatures = geojsonData.features.filter((feature) => {
      return Object.keys(filters).every((key) => {
        if (filters[key]) {
          return checkTrue(feature.properties[key]);
        }
        return true;
      });
    });

    setFilteredGeojsonData({
      ...geojsonData,
      features: filteredFeatures,
    });
  }, [filters, geojsonData]);

  useEffect(() => {
    if (!filteredGeojsonData) return;

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: 'mapbox://styles/mapbox/satellite-streets-v11',
      center: [0, 0],
      zoom: 2.5,
    });
    
    map.on('load', () => {
      map.addSource('ports', {
        type: 'geojson',
        data: filteredGeojsonData,
      });

      map.addLayer({
        id: 'ports-layer',
        type: 'circle',
        source: 'ports',
        paint: {
          'circle-radius': 8,
          'circle-color': [
            'case',
            ['==', ['get', 'CleaningInPort'], 'TRUE'], // If CleaningInPort is TRUE
            '#28a745', // Green
            '#dc3545', // Red
          ],
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff',
        },
      });

      map.on('click', 'ports-layer', (e) => {
        const coordinates = e.features[0].geometry.coordinates.slice();
        const { portName, country, portComment, isAnchorage, ...conditions } = e.features[0].properties;
        console.log('Selected Port Conditions:', conditions);
        setSelectedPort({
          portName,
          country,
          portComment,
          isAnchorage,
          conditions,
        });
      });

      map.on('mouseenter', 'ports-layer', () => {
        map.getCanvas().style.cursor = 'pointer';
      });

      map.on('mouseleave', 'ports-layer', () => {
        map.getCanvas().style.cursor = '';
      });

      // Add the legend popup
      const legend = document.createElement('div');
      legend.className = 'map-legend';
      legend.innerHTML = `
        <div><span class="legend-icon" style="background-color: #28a745;"></span> Cleaning Available</div>
        <div><span class="legend-icon" style="background-color: #dc3545;"></span> Cleaning Not Available</div>
      `;
      map.getContainer().appendChild(legend);
    });

    return () => map.remove();
  }, [filteredGeojsonData]);

  const handleFilterChange = (e) => {
    const { name, checked } = e.target;
    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: checked,
    }));
  };

  const clearFilters = () => {
    setFilters({
      CleaningInPort: false,
      InspectionInPort: false,
      GeneralDivingInPort: false,
      PropellerPolishingInPort: false,
    });
  };

  return (
    <div>
      <h1>Port Locations Map</h1>

      <div ref={mapContainerRef} className="map-container" />

      <div style={{ textAlign: 'center', marginTop: '30px' }}>
        <button onClick={() => setDropdownOpen(!dropdownOpen)} className="filter-button">
          Filter
        </button>
        {dropdownOpen && (
          <div className="filter-dropdown">
            <h3 className="filter-title">Select Conditions</h3>
            <div className="filter-options">
              {Object.keys(filters).map((filter) => (
                <label key={filter}>
                  <input
                    type="checkbox"
                    name={filter}
                    checked={filters[filter]}
                    onChange={handleFilterChange}
                  />
                  {filter.replace(/([A-Z])/g, ' $1')}
                </label>
              ))}
            </div>
            <div style={{ marginTop: '10px' }}>
              <button onClick={clearFilters} className="clear-button">
                Clear Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {selectedPort && (
        <div className="modal" ref={modalRef}>
          <div className="modal-content">
            <h2>{selectedPort.portName}</h2>
            <p>
              <strong>Country:</strong> {selectedPort.country}
            </p>
            <p>
              <strong>Port Comment:</strong> {selectedPort.portComment}
            </p>
            <p>
              <strong>Has Anchorage:</strong> {selectedPort.isAnchorage ? 'Yes' : 'No'}
            </p>

            <div className="condition-grid">
              {Object.keys(selectedPort.conditions)
                .filter((condition) => !['AliciaBots', 'GreenSea', 'DGDiving', 'HullBot'].includes(condition))
                .map((condition) => (
                  <div className="condition-item" key={condition}>
                    <span>{condition.replace(/([A-Z])/g, ' $1')}</span>
                    <span className={checkTrue(selectedPort.conditions[condition]) ? 'dot-true' : 'dot-false'}></span>
                  </div>
                ))}
            </div>

            <h3>Grooming Services Available</h3>
            <div className="additional-services-grid">
              {['AliciaBots', 'GreenSea', 'DGDiving', 'HullBot']
                .filter((service) => checkTrue(selectedPort.conditions[service]))
                
                .map((service) => (
                  <div className="additional-service-item" key={service}>
                    <img
                      src={`/${service}.png`}
                      alt={service}
                      className="service-logo"
                    />
                    <span className="service-name">
                      {service === 'GreenSea' ? 'GreenSea IQ' : service.replace(/([A-Z])/g, ' $1')}
                    </span>
                  </div>
                ))}
            </div>
            
          </div>
        </div>
      )}
    </div>
  );
};

export default Port;
